import { Flex, Grid, GridItem } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import { TimePicker } from '@televet/kibble-ui/build/components/TimePicker';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Popover } from '@televet/kibble-ui/build/components/Popover';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { Button } from '@televet/kibble-ui/build/components/Button';

interface PayoutSwitchProps {
  endsAt: string;
  initialTime: Date;
  updateBatchingPeriod: (params: { endsAt: string; time: Date; isChecked: boolean }) => void;
  handleApplyTimeToAllDays?: (time: Date) => void;
  isToday?: boolean;
  isSelected: boolean;
}

const PayoutSwitch: React.FC<PayoutSwitchProps> = ({
  endsAt,
  initialTime,
  updateBatchingPeriod,
  handleApplyTimeToAllDays,
  isToday,
  isSelected,
}) => {
  const [isChecked, setIsChecked] = useState(isSelected);
  const [time, setTime] = useState(initialTime);

  useEffect(() => {
    setTime(initialTime);
    setIsChecked(isSelected);
  }, [initialTime, isSelected]);

  const formattedDate = useMemo(() => {
    // date strings from backend are in UTC, to avoid mismatching dates, convert to UTC before formatting
    const zonedDate = utcToZonedTime(endsAt, 'UTC');
    return format(zonedDate, 'EEEE, MMMM do');
  }, [endsAt]);

  const handleTimeChange = useCallback(
    (newTime: Date) => {
      const adjustedNewTime = initialTime.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);
      const adjustedNewDate = new Date(adjustedNewTime);
      setTime(adjustedNewDate);
      updateBatchingPeriod({ endsAt, time: adjustedNewDate, isChecked });
    },
    [endsAt, initialTime, isChecked, updateBatchingPeriod],
  );

  const handleSwitchChange = useCallback(() => {
    const newCheckedState = !isChecked;
    setIsChecked(newCheckedState);
    updateBatchingPeriod({ endsAt, time, isChecked: newCheckedState });
  }, [isChecked, endsAt, time, updateBatchingPeriod]);

  return (
    <Grid role="group" className="Schedule__Day" alignItems="center" templateColumns="repeat(6, 1fr)" gap={4}>
      <GridItem colSpan={3}>
        <Popover
          anchor={
            <Flex alignItems="center" gap={2}>
              {!isChecked && !isToday && (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  bgColor="background.accentSubtle"
                  py={1}
                  px={2}
                  borderRadius="base"
                >
                  <Icon name="arrowUp" size="sm" />
                </Flex>
              )}
              <Text variant={!isChecked ? 'subtle' : 'default'}>{formattedDate}</Text>
            </Flex>
          }
          placement="top"
          trigger="hover"
          size="lg"
          {...((isChecked || isToday) && { isOpen: false })}
        >
          <Text size="sm" variant="onContrast">
            Transactions from{' '}
            <Text as="span" size="sm" fontWeight="semibold" variant="onContrast">
              {formattedDate}
            </Text>{' '}
            will be included in the end of day report for the next Payout Day.
          </Text>
        </Popover>
      </GridItem>
      <GridItem colSpan={2}>
        <Flex gap={1}>
          <TimePicker
            key={`${endsAt}-${isChecked}`}
            inputProps={{
              rightElement: undefined,
              isDisabled: !isChecked,
              placeholder: '--',
            }}
            placement="bottom"
            initialTime={isChecked ? time : undefined}
            minuteInterval={1}
            onTimeChange={handleTimeChange}
          />
          <Tooltip label="Apply time to all days">
            <Button
              iconName="bulkSelect"
              variant="ghost"
              visibility="hidden"
              {...(isChecked &&
                handleApplyTimeToAllDays && {
                  _groupHover: {
                    visibility: 'visible',
                  },
                })}
              onClick={(): void => {
                if (handleApplyTimeToAllDays) {
                  handleApplyTimeToAllDays(time);
                }
              }}
            />
          </Tooltip>
        </Flex>
      </GridItem>
      <GridItem>
        <Switch isChecked={isChecked} onChange={handleSwitchChange} />
      </GridItem>
    </Grid>
  );
};

export default PayoutSwitch;
